// src/pages/MissingAgent.js
import React, { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { calculateCommission, DEFAULT_COMP_PLAN } from '../data/compPlans';

export default function MissingAgent() {
  const [orders, setOrders] = useState([]);
  const [reps, setReps] = useState([]);
  const [compPlans, setCompPlans] = useState([DEFAULT_COMP_PLAN]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [search, setSearch] = useState('');
  const [assignments, setAssignments] = useState({});

  useEffect(() => {
    async function load() {
      const [ordSnap, repSnap, planSnap] = await Promise.all([
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'compPlans')),
      ]);
      const allOrders = ordSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Filter client-side — Firestore null queries are unreliable on large collections
      const unmatched = allOrders.filter(o => !o.repId || o.repId === null || o.repId === '');
      setOrders(unmatched);
      setReps(repSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const dbPlans = planSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (dbPlans.length) setCompPlans([DEFAULT_COMP_PLAN, ...dbPlans]);
      setLoading(false);
    }
    load();
  }, []);

  async function assignAgent(orderId, repId) {
    if (!repId) return;
    setSaving(s => ({ ...s, [orderId]: true }));
    const rep = reps.find(r => r.id === repId);
    const order = orders.find(o => o.id === orderId);
    const overrides = [{ repId, role: rep.role || 'Agent' }];
    const commissions = calculateCommission(
      { productId: order.productId, quantity: order.quantity || 1, overrides, compPlanId: rep.compPlanId || DEFAULT_COMP_PLAN.id },
      reps, compPlans
    );
    const totalCommission = commissions.reduce((a, c) => a + c.amount, 0);
    await updateDoc(doc(db, 'orders', orderId), {
      repId, repName: rep.name, overrides, commissions, totalCommission, heldFromPayroll: false,
    });
    setOrders(prev => prev.filter(o => o.id !== orderId));
    setSaving(s => ({ ...s, [orderId]: false }));
  }

  async function holdOrder(orderId, held) {
    await updateDoc(doc(db, 'orders', orderId), { heldFromPayroll: held });
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, heldFromPayroll: held } : o));
  }

  const filtered = orders.filter(o => {
    const q = search.toLowerCase();
    return !q || o.customerName?.toLowerCase().includes(q) || o.productName?.toLowerCase().includes(q) || o.agentCode?.toLowerCase().includes(q);
  });

  if (loading) return <div style={{ padding: 60, color: '#555' }}>Loading...</div>;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Missing agent</h1>
          <p style={S.sub}>{orders.length} orders with no rep assigned · assign or hold for later payroll</p>
        </div>
      </div>

      <div style={S.infoBox}>
        Orders held from payroll will not appear in weekly payroll calculations until a rep is assigned. You can assign reps at any time and they'll be paid in the next payroll run.
      </div>

      <div style={S.toolbar}>
        <input style={S.input} placeholder="Search by customer, product, or agent code..." value={search} onChange={e => setSearch(e.target.value)} />
        <div style={S.countBadge}>{filtered.length} orders</div>
      </div>

      <div style={S.tableCard}>
        <table style={S.table}>
          <thead>
            <tr>{['Customer', 'Product', 'Agent code', 'Status', 'Date', 'Assign rep', 'Hold'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.map(o => (
              <tr key={o.id} style={{ opacity: o.heldFromPayroll ? 0.5 : 1 }}>
                <td style={S.td}>{o.customerName}</td>
                <td style={{ ...S.td, color: '#888' }}>{o.productName}</td>
                <td style={{ ...S.td, color: '#f5a623' }}>{o.agentCode || '—'}</td>
                <td style={S.td}>
                  <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 5, background: o.status === 'Completed' ? '#1a2e24' : '#1a2a3a', color: o.status === 'Completed' ? '#00d4a0' : '#5599cc' }}>
                    {o.status}
                  </span>
                </td>
                <td style={{ ...S.td, color: '#555' }}>{o.date || '—'}</td>
                <td style={S.td}>
                  <div style={S.assignRow}>
                    <select
                      style={S.assignSelect}
                      value={assignments[o.id] || ''}
                      onChange={e => setAssignments(a => ({ ...a, [o.id]: e.target.value }))}
                    >
                      <option value="">— select rep —</option>
                      {reps.sort((a,b) => a.name?.localeCompare(b.name)).map(r => (
                        <option key={r.id} value={r.id}>{r.name} ({r.agentCode || 'no code'})</option>
                      ))}
                    </select>
                    <button
                      style={{ ...S.assignBtn, opacity: assignments[o.id] ? 1 : 0.4 }}
                      disabled={!assignments[o.id] || saving[o.id]}
                      onClick={() => assignAgent(o.id, assignments[o.id])}
                    >
                      {saving[o.id] ? '...' : 'Assign'}
                    </button>
                  </div>
                </td>
                <td style={S.td}>
                  <button
                    style={{ ...S.holdBtn, background: o.heldFromPayroll ? '#2a5a3a' : '#2a2010', color: o.heldFromPayroll ? '#00d4a0' : '#f5a623' }}
                    onClick={() => holdOrder(o.id, !o.heldFromPayroll)}
                  >
                    {o.heldFromPayroll ? 'Held' : 'Hold'}
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', color: '#444', padding: 40 }}>
                {orders.length === 0 ? 'No unmatched orders — all orders have reps assigned!' : 'No orders match your search'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const S = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 13, color: '#555', margin: '4px 0 0' },
  infoBox: { background: '#1a2010', border: '1px solid #3a4020', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#aaa', marginBottom: 16, lineHeight: 1.6 },
  toolbar: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 },
  input: { background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', width: 320 },
  countBadge: { fontSize: 12, color: '#555', background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 6, padding: '6px 12px' },
  tableCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 14px', fontSize: 11, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #1e1e2a' },
  td: { padding: '10px 14px', fontSize: 13, color: '#ccc', borderBottom: '1px solid #111' },
  assignRow: { display: 'flex', gap: 6 },
  assignSelect: { background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 6, padding: '5px 8px', color: '#fff', fontSize: 12, flex: 1 },
  assignBtn: { background: '#00d4a0', border: 'none', borderRadius: 6, padding: '5px 10px', color: '#000', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  holdBtn: { border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 500, cursor: 'pointer' },
};
